// COPIA DICHIARATA di src/components/scopa/TavoliMieiScopa.tsx @ e99b95115c26c5085335a527db6cb22b23fd2f81 (TMA#735, 2026-10-20)
// «I MIEI TAVOLI / RIPRENDI» di un gioco del kit: i tavoli ancora vivi di chi guarda. «Riprendi» porta alla rotta del tavolo del gioco, che chiede un ticket NUOVO come
// sempre. I cinque esiti hanno ognuno la sua frase: in lettura, `non-disponibile` (senza «Riprova»), guasto con «Riprova», una risposta che non ha la forma (un guasto,
// MAI «nessun tavolo»), l'elenco vuoto vero.
// Cosa cambia rispetto alla fonte, oltre al gioco che diventa parametro:
//   · la lettura non e piu l hook RTK del gioco scritto qui: la fa `useTavoli()`, che il gioco passa (la sua `use<Gioco>MyTablesQuery(undefined, { refetchOnMountOrArgChange: true })`,
//     una funzione di MODULO, stabile); `data`, `error`, `isFetching` e `refetch` si leggono come prima;
//   · la rotta di «Riprendi» la compone `rottaTavolo(tableId)` (dove la fonte scriveva la rotta del suo gioco);
//   · i TESTI non stanno qui: il componente non ha `useTranslation` ne frasi scritte, tutte le etichette arrivano in `etichette` (gia tradotte dal gioco, dal suo catalogo: nessuna
//     chiave nuova, nessun catalogo nuovo); `nomeTavolo(posti)` rende il nome del formato (dove la fonte sceglieva fra due chiavi per 4 e 2 posti);
//   · il marcatore `data-testid` e l'`id` del titolo (che nella fonte portavano il nome del gioco) sono `tavoli-miei` e un `useId()`;
//   · i marcatori `data-tavoli-vuoto`, `data-tavoli-guasto`, `data-tavolo`, la struttura, le classi e gli esiti sono IDENTICI.
import { useId } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/common/Button';
import { PanelWaiting } from '@/components/hub/PanelNotice';
import { useStalled } from '@/hooks/useStalled';
import { guastoOf, type TavoliMiei as ElencoTavoli, type TavoloMio } from '@/lib/gamehost/rest';

/** Cio che la lettura dei miei tavoli rende: la forma di una query RTK, per la parte che serve qui. */
export interface RisultatoTavoli {
  data?: ElencoTavoli | undefined;
  error?: unknown;
  isFetching: boolean;
  refetch: () => unknown;
}

/** I testi, GIA tradotti dal gioco. `nomeTavolo` rende il formato dai posti (`null` = un numero di posti che il gioco non conosce). */
export interface EtichetteTavoli {
  titolo: string;
  vuoto: string;
  inCorso: string;
  inAttesa: string;
  riprendi: string;
  riprova: string;
  guasto: { 'non-disponibile': string; rete: string; errore: string };
  nomeTavolo: (posti: number | null) => string;
}

export interface TavoliMieiProps {
  /** La lettura: una funzione di MODULO (stabile), chiamata come un hook a ogni render. */
  useTavoli: () => RisultatoTavoli;
  /** La rotta del tavolo del gioco, dall'uuid minuscolo. */
  rottaTavolo: (tableId: string) => string;
  etichette: EtichetteTavoli;
}

export function TavoliMiei({ useTavoli, rottaTavolo, etichette }: TavoliMieiProps) {
  const navigate = useNavigate();
  const titoloId = useId();
  const { data, error, isFetching, refetch } = useTavoli();
  const lenta = useStalled(data === undefined && error === undefined);

  let corpo;
  if (error !== undefined && data === undefined) {
    const g = guastoOf(error);
    corpo = <Avviso etichette={etichette} guasto={g === 'non-disponibile' ? g : g === 'rete' ? 'rete' : 'errore'} ripetendo={isFetching} onRiprova={() => void refetch()} />;
  } else if (data === undefined) {
    corpo = <PanelWaiting slow={lenta} className="h-auto py-4" />;
  } else if (!data.ok) {
    corpo = <Avviso etichette={etichette} guasto="errore" ripetendo={isFetching} onRiprova={() => void refetch()} />;
  } else if (data.tavoli.length === 0) {
    corpo = (
      <p data-tavoli-vuoto className="rounded-xl bg-bg-secondary px-4 py-3 text-sm text-fg-muted">
        {etichette.vuoto}
      </p>
    );
  } else {
    corpo = (
      <ul className="flex flex-col gap-2">
        {data.tavoli.map((tavolo) => (
          <RigaTavolo key={tavolo.tableId} tavolo={tavolo} etichette={etichette} onRiprendi={() => navigate(rottaTavolo(tavolo.tableId))} />
        ))}
      </ul>
    );
  }

  return (
    <section data-testid="tavoli-miei" aria-labelledby={titoloId} className="flex shrink-0 flex-col gap-2">
      <h2 id={titoloId} className="text-xs font-bold uppercase tracking-wide text-fg-muted">
        {etichette.titolo}
      </h2>
      {corpo}
    </section>
  );
}

function RigaTavolo({ tavolo, etichette, onRiprendi }: { tavolo: TavoloMio; etichette: EtichetteTavoli; onRiprendi: () => void }) {
  const stato = tavolo.attivo ? etichette.inCorso : etichette.inAttesa;
  return (
    <li data-tavolo={tavolo.tableId} className="card-edge flex items-center gap-3 rounded-xl bg-bg-secondary p-3">
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="break-words text-sm font-semibold leading-5 text-fg">{etichette.nomeTavolo(tavolo.posti)}</span>
        <span className="break-words text-xs leading-4 text-fg-muted">{stato}</span>
      </span>
      <Button variant="primary" className="shrink-0" onClick={onRiprendi}>
        {etichette.riprendi}
      </Button>
    </li>
  );
}

function Avviso({ etichette, guasto, ripetendo, onRiprova }: { etichette: EtichetteTavoli; guasto: 'non-disponibile' | 'rete' | 'errore'; ripetendo: boolean; onRiprova: () => void }) {
  return (
    <div role="status" data-tavoli-guasto={guasto} className="flex flex-col items-start gap-2 rounded-xl bg-bg-secondary px-4 py-3">
      <p className="text-sm text-fg-muted">{etichette.guasto[guasto]}</p>
      {guasto !== 'non-disponibile' && (
        <Button variant="secondary" disabled={ripetendo} onClick={onRiprova}>
          {etichette.riprova}
        </Button>
      )}
    </div>
  );
}
